import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const behaviorOptions = [
  { value: 'external', label: 'Dış Link' },
  { value: 'none', label: 'Tıklama Yok' },
];

const statusOptions = [
  { value: 'published', label: 'Yayında' },
  { value: 'scheduled', label: 'Zamanlandı' },
  { value: 'archived', label: 'Arşivde' },
];

const normalizeStatus = (s) => {
  if (s === 'archieved') return 'archived';
  return s || 'published';
};

const toDayjs = (v) => (v ? dayjs(v) : null);

export default function AnnouncementForm({ initialData, categories, submitting, onSubmit, onCancel }) {
  const [form, setForm] = useState({
    title: '',
    category_id: '',
    slug: '',
    content: '',
    link_url: '',
    click_behavior: 'none',
    link_label: '',
    pinned: false,
    status: 'published',
    publish_at: dayjs(),
  });
  const [error, setError] = useState('');
  const sectionStyle = {
    p: 2.5,
    borderRadius: 2,
    borderColor: '#e4e9f2',
    background: '#f9fbff',
  };
  const baseFieldSx = { '& .MuiInputBase-root': { minHeight: 44 } };

  useEffect(() => {
    if (!initialData) return;
    const normalizedStatus = normalizeStatus(initialData.status);
    setForm({
      title: initialData.title || '',
      category_id: initialData.category?.id || '',
      slug: initialData.slug || '',
      content: initialData.content || '',
      link_url: initialData.link_url || '',
      click_behavior: initialData.click_behavior || 'none',
      link_label: initialData.link_label || '',
      pinned: Boolean(initialData.pinned),
      status: normalizedStatus,
      publish_at: toDayjs(initialData.publish_at) || dayjs(),
    });
  }, [initialData]);

  const categoryItems = useMemo(() => categories || [], [categories]);

  const handleChange = (field) => (e) => {
    const value = e?.target?.value;
    setForm((cur) => ({ ...cur, [field]: value }));
  };

  const handleBehaviorChange = (e) => {
    const value = e?.target?.value;
    setForm((cur) => ({
      ...cur,
      click_behavior: value,
      link_url: value === 'external' ? cur.link_url : '',
      link_label: value === 'external' ? cur.link_label : '',
    }));
  };

  const handleStatusChange = (e) => {
    const value = e?.target?.value;
    setForm((cur) => ({
      ...cur,
      status: value,
      publish_at: value === 'scheduled' ? cur.publish_at || dayjs() : dayjs(),
    }));
  };

  const handleDateChange = (field) => (value) => {
    setForm((cur) => ({ ...cur, [field]: value }));
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.title.trim()) {
      setError('Başlık zorunludur.');
      return;
    }
    if (form.click_behavior === 'external' && !form.link_url.trim()) {
      setError('Dış link için bağlantı URL\'si zorunludur.');
      return;
    }
    if (form.status === 'scheduled') {
      const publish = form.publish_at || dayjs();
      if (publish.isBefore(dayjs())) {
        setError('Yayınlanma zamanı geçmiş olamaz.');
        return;
      }
    }
    const payload = {
      title: form.title.trim(),
      category_id: form.category_id ? Number(form.category_id) : undefined,
      slug: form.slug?.trim() || undefined,
      content: form.content?.trim() || undefined,
      link_url: form.click_behavior === 'external' ? form.link_url?.trim() || undefined : undefined,
      click_behavior: form.click_behavior || 'none',
      link_label: form.click_behavior === 'external' ? form.link_label?.trim() || undefined : undefined,
      pinned: Boolean(form.pinned),
      status: form.status || 'scheduled',
      publish_at: form.status === 'scheduled' && form.publish_at ? form.publish_at.toISOString() : undefined,
    };
    await onSubmit?.(payload);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack spacing={2.5}>
        <Paper variant="outlined" sx={sectionStyle}>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle1" fontWeight={700}>Temel Bilgiler</Typography>
              <Chip label="Zorunlu alanlar" size="small" />
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Başlık"
                  value={form.title}
                  onChange={handleChange('title')}
                  fullWidth
                  required
                  size="small"
                  sx={baseFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Slug (opsiyonel)"
                  value={form.slug}
                  onChange={handleChange('slug')}
                  fullWidth
                  helperText="Boş bırakırsan başlıktan üretilecek."
                  size="small"
                  sx={baseFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                  <InputLabel id="category-label" shrink>Kategori</InputLabel>
                  <Select
                    labelId="category-label"
                    label="Kategori"
                    value={form.category_id || ''}
                    onChange={handleChange('category_id')}
                    displayEmpty
                    renderValue={(val) => {
                      if (!val) return <span style={{ color: '#9aa7b0' }}>Seçilmedi</span>;
                      const found = categoryItems.find((c) => String(c.id) === String(val));
                      return found ? found.name : val;
                    }}
                  >
                    <MenuItem value="">
                      Seçilmedi
                    </MenuItem>
                    {categoryItems.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={sectionStyle}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>İçerik</Typography>
            <TextField
              label="İçerik"
              value={form.content}
              onChange={handleChange('content')}
              fullWidth
              multiline
              minRows={8}
              sx={{ '& .MuiInputBase-root': { minHeight: 200 } }}
              size="small"
            />
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={sectionStyle}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Tıklama & Bağlantı</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                  <InputLabel id="behavior-label">Tıklama Davranışı</InputLabel>
                  <Select
                    labelId="behavior-label"
                    label="Tıklama Davranışı"
                    value={form.click_behavior}
                    onChange={handleBehaviorChange}
                    renderValue={(val) => behaviorOptions.find((b) => b.value === val)?.label || val}
                  >
                    {behaviorOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                  {form.click_behavior === 'external' && (
                    <FormHelperText>Dış link için URL zorunlu.</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={5}>
                <TextField
                  label="Bağlantı URL'si"
                  value={form.link_url}
                  onChange={handleChange('link_url')}
                  fullWidth
                  placeholder="https://..."
                  size="small"
                  sx={baseFieldSx}
                  disabled={form.click_behavior !== 'external'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Bağlantı Etiketi"
                  value={form.link_label}
                  onChange={handleChange('link_label')}
                  fullWidth
                  size="small"
                  sx={baseFieldSx}
                  disabled={form.click_behavior !== 'external'}
                />
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={sectionStyle}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>Durum & Zamanlama</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small" sx={{ minWidth: 0 }}>
                  <InputLabel id="status-label">Durum</InputLabel>
                  <Select
                    labelId="status-label"
                    label="Durum"
                    value={form.status}
                    onChange={handleStatusChange}
                    renderValue={(val) => statusOptions.find((s) => s.value === val)?.label || val}
                  >
                    {statusOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePicker
                  label="Yayınlanma Zamanı"
                  value={form.publish_at}
                  onChange={handleDateChange('publish_at')}
                  disablePast={form.status === 'scheduled'}
                  disabled={form.status !== 'scheduled'}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      InputProps: { readOnly: form.status !== 'scheduled' },
                      error: false,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3} display="flex" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.pinned}
                      onChange={(e) => setForm((cur) => ({ ...cur, pinned: e.target.checked }))}
                      size="small"
                    />
                  }
                  label="Üste Sabitle"
                  sx={{ ml: 1 }}
                />
              </Grid>
            </Grid>
          </Stack>
        </Paper>

        {error && <Typography color="error" variant="body2">{error}</Typography>}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" onClick={onCancel} disabled={submitting}>Vazgeç</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {initialData ? 'Güncelle' : 'Oluştur'}
          </Button>
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
}
